import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSearch } from '../../components/Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DOCTOR_TITLE_LEVELS = new Set(['Doctorate Degree', 'MBChB']);

const getHighestEducationalLevel = (education) => {
  if (!education) return '';
  if (typeof education === 'string') return education;
  if (Array.isArray(education)) {
    const current = education.find((item) => item?.level || item?.highestEducationalLevel || item?.degree);
    return current?.level || current?.highestEducationalLevel || current?.degree || '';
  }
  return education.level || education.highestEducationalLevel || education.degree || '';
};

const getPractitionerDisplayName = (user) => {
  const surname = user?.lastName || user?.firstName || 'Practitioner';
  const level = getHighestEducationalLevel(user?.education);
  return DOCTOR_TITLE_LEVELS.has(level) ? `Dr. ${surname}` : surname;
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const getInitials = (client) => `${client?.firstName?.[0] || ''}${client?.lastName?.[0] || ''}`.toUpperCase() || 'CL';
const getRiskLabel = (risk) => risk?.level || 'low';

const MobileDashboard = () => {
  const navigate = useNavigate();
  const { user, isApproved } = useAuth();
  const { searchQuery } = useSearch();
  const [stats, setStats] = useState(null);
  const [recentResults, setRecentResults] = useState([]);
  const [followUpCandidates, setFollowUpCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      if (!isApproved) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${API_URL}/practitioner/dashboard`);
        setStats(res.data.stats || {});
        setRecentResults(res.data.recentResults || []);
        setFollowUpCandidates(res.data.followUpCandidates || []);
      } catch (error) {
        console.error('Mobile dashboard load error:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [isApproved]);

  const filteredResults = useMemo(() => {
    if (!searchQuery) return recentResults;
    const q = searchQuery.toLowerCase();
    return recentResults.filter(result => {
      const clientName = `${result.Client?.firstName || ''} ${result.Client?.lastName || ''}`.toLowerCase();
      const testName = result.TestCatalog?.shortName?.toLowerCase() || '';
      const status = result.status?.toLowerCase() || '';
      const date = result.completedAt ? new Date(result.completedAt).toLocaleDateString().toLowerCase() : '';
      return clientName.includes(q) || testName.includes(q) || status.includes(q) || date.includes(q);
    });
  }, [recentResults, searchQuery]);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  const priorityFollowUp = followUpCandidates[0];

  if (!isApproved) {
    return (
      <MobilePage>
        <PendingCard>
          <PendingIcon>⌛</PendingIcon>
          <h2>Account pending approval</h2>
          <p>Your practitioner account is under review. We will notify you when mobile access is fully enabled.</p>
          <StatusChip>Status: {user?.approvalStatus || 'Pending'}</StatusChip>
        </PendingCard>
      </MobilePage>
    );
  }

  if (loading) {
    return (
      <MobilePage>
        <HeroSkeleton />
        <MetricRow>
          {[0, 1, 2, 3].map(item => <MetricSkeleton key={item} />)}
        </MetricRow>
        <CardSkeleton />
        <CardSkeleton />
      </MobilePage>
    );
  }

  return (
    <MobilePage>
      <HeroCard>
        <HeroText>
          <LivePill><span /> Dashboard Overview</LivePill>
          <h2>{greeting}, {getPractitionerDisplayName(user)}</h2>
          <p>Track clients, monitor assessments, and manage your workflow.</p>
        </HeroText>
        <HeroIllustration aria-hidden="true">
          <div className="chart-card">
            <span />
            <span />
            <span />
          </div>
          <div className="pulse-ring">✓</div>
        </HeroIllustration>
      </HeroCard>

      <SectionTitle>Frontline summary</SectionTitle>
      <MetricGrid aria-label="Dashboard metrics">
        <MetricCard $accent="#2563eb">
          <span>Total clients</span>
          <strong>{stats?.totalClients || 0}</strong>
          <small>Active caseload</small>
        </MetricCard>
        <MetricCard $accent="#059669">
          <span>Completed</span>
          <strong>{stats?.totalTests || 0}</strong>
          <small>All assessments</small>
        </MetricCard>
        <MetricCard $accent="#d97706">
          <span>Pending</span>
          <strong>{stats?.pendingTests || 0}</strong>
          <small>Awaiting clients</small>
        </MetricCard>
        <MetricCard $accent="#dc2626">
          <span>Follow ups</span>
          <strong>{stats?.followUpFlags || 0}</strong>
          <small>Priority review</small>
        </MetricCard>
        <MetricCard $accent="#7c3aed">
          <span>Earnings</span>
          <strong>₵{stats?.totalEarnings ? parseFloat(stats.totalEarnings).toFixed(2) : '0.00'}</strong>
          <small>Revenue</small>
        </MetricCard>
      </MetricGrid>

      <ActionDeck>
        <SectionHeader>
          <div>
            <span>Shortcut hub</span>
            <h3>Quick Actions</h3>
          </div>
        </SectionHeader>
        <ActionGrid>
          <ActionTile type="button" onClick={() => navigate('/send-test')} $tone="blue">
            <span>✈</span>
            <strong>Send Test</strong>
            <small>Invite a client</small>
          </ActionTile>
          <ActionTile type="button" onClick={() => navigate('/results')} $tone="purple">
            <span>▣</span>
            <strong>View Results</strong>
            <small>Check assessments</small>
          </ActionTile>
          <ActionTile type="button" onClick={() => navigate('/earnings')} $tone="green">
            <span>₵</span>
            <strong>Earnings</strong>
            <small>Revenue details</small>
          </ActionTile>
          <ActionTile type="button" onClick={() => navigate('/profile')} $tone="amber">
            <span>⚙</span>
            <strong>Settings</strong>
            <small>Manage profile</small>
          </ActionTile>
        </ActionGrid>
      </ActionDeck>

      {priorityFollowUp && (
        <PriorityCard>
          <SectionHeader>
            <div>
              <span>Priority</span>
              <h3>Needs follow-up</h3>
            </div>
            <TextButton type="button" onClick={() => navigate('/flagged-follow-ups')}>View all</TextButton>
          </SectionHeader>
          <PriorityBody>
            <ClientAvatar>{getInitials(priorityFollowUp.Client)}</ClientAvatar>
            <div>
              <strong>{priorityFollowUp.Client?.firstName} {priorityFollowUp.Client?.lastName}</strong>
              <p>{priorityFollowUp.TestCatalog?.shortName || 'Assessment'} • {formatDate(priorityFollowUp.completedAt)}</p>
              <RiskPill level={getRiskLabel(priorityFollowUp.followUpRisk)}>{getRiskLabel(priorityFollowUp.followUpRisk)}</RiskPill>
            </div>
          </PriorityBody>
          <Reason>{priorityFollowUp.followUpRisk?.reasons?.[0] || priorityFollowUp.followUpRisk?.recommendedAction || 'Review recommended client follow-up.'}</Reason>
        </PriorityCard>
      )}

      <RecentCard>
        <SectionHeader>
          <div>
            <span>Latest activity</span>
            <h3>Recent results</h3>
          </div>
          <TextButton type="button" onClick={() => navigate('/results')}>View all</TextButton>
        </SectionHeader>

        {filteredResults.length > 0 ? (
          <ResultList>
            {filteredResults.slice(0, 6).map(result => (
              <ResultItem key={result.id} type="button" onClick={() => navigate('/results')}>
                <ClientAvatar>{getInitials(result.Client)}</ClientAvatar>
                <ResultCopy>
                  <strong>{result.Client?.firstName} {result.Client?.lastName}</strong>
                  <span>{result.TestCatalog?.shortName || 'Assessment'} • {formatDate(result.completedAt)}</span>
                </ResultCopy>
                <ResultStatus $paid={result.isPaid}>{result.isPaid ? 'Paid' : 'Done'}</ResultStatus>
              </ResultItem>
            ))}
          </ResultList>
        ) : (
          <EmptyState>
            <strong>{searchQuery ? 'No mobile results found' : 'No results yet'}</strong>
            <p>{searchQuery ? `Nothing matched “${searchQuery}”.` : 'Send your first assessment to start tracking client progress.'}</p>
          </EmptyState>
        )}
      </RecentCard>
    </MobilePage>
  );
};

const MobilePage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const HeroCard = styled.section`
  align-items: center;
  background:
    radial-gradient(circle at top right, rgba(103, 232, 249, 0.34), transparent 42%),
    linear-gradient(135deg, #0c2d48 0%, #1e40af 52%, #0284c7 100%);
  border-radius: 30px;
  box-shadow: 0 20px 48px rgba(30, 64, 175, 0.24);
  color: white;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 104px;
  gap: 12px;
  overflow: hidden;
  padding: 21px;
`;

const HeroText = styled.div`
  min-width: 0;

  h2 { font-size: clamp(1.34rem, 8vw, 1.82rem); line-height: 1.08; margin: 12px 0 9px; }
  p { color: rgba(255, 255, 255, 0.8); font-size: 0.88rem; line-height: 1.48; margin: 0; }
`;

const LivePill = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 850;
  gap: 7px;
  padding: 6px 10px;

  span { background: #67e8f9; border-radius: 50%; height: 8px; width: 8px; }
`;

const HeroIllustration = styled.div`
  align-items: center;
  display: flex;
  height: 112px;
  justify-content: center;
  position: relative;

  .chart-card {
    align-items: end;
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 24px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
    display: flex;
    gap: 7px;
    height: 88px;
    justify-content: center;
    padding: 18px 15px;
    width: 88px;
  }

  .chart-card span {
    background: linear-gradient(180deg, #bae6fd, #ffffff);
    border-radius: 999px;
    display: block;
    width: 9px;
  }

  .chart-card span:nth-of-type(1) { height: 34px; opacity: 0.78; }
  .chart-card span:nth-of-type(2) { height: 52px; }
  .chart-card span:nth-of-type(3) { height: 42px; opacity: 0.88; }

  .pulse-ring {
    align-items: center;
    background: #67e8f9;
    border: 4px solid rgba(255, 255, 255, 0.25);
    border-radius: 50%;
    bottom: 4px;
    box-shadow: 0 12px 24px rgba(8, 145, 178, 0.24);
    color: #075985;
    display: flex;
    font-weight: 950;
    height: 36px;
    justify-content: center;
    position: absolute;
    right: 4px;
    width: 36px;
  }
`;

const SectionTitle = styled.h3`
  color: #0f172a;
  font-size: 0.96rem;
  margin: 2px 2px -4px;
`;

const MetricGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const MetricCard = styled.article`
  background: white;
  border: 1px solid #e8f0fe;
  border-radius: 22px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  min-height: 116px;
  padding: 15px;
  position: relative;

  &::before {
    background: ${p => p.$accent};
    border-radius: 99px;
    content: '';
    height: 5px;
    left: 15px;
    position: absolute;
    right: 15px;
    top: 0;
  }

  span { color: #64748b; display: block; font-size: 0.76rem; font-weight: 850; margin-top: 5px; }
  strong { color: #0f172a; display: block; font-size: clamp(1.35rem, 8vw, 1.75rem); line-height: 1.1; margin-top: 12px; overflow-wrap: anywhere; }
  small { color: #94a3b8; display: block; font-size: 0.72rem; font-weight: 750; margin-top: 6px; }
`;

const ActionDeck = styled.section`
  background: white;
  border: 1px solid #e8f0fe;
  border-radius: 26px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
  padding: 12px;
`;

const ActionGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const ActionTile = styled.button`
  background: ${p => ({
    blue: 'linear-gradient(135deg, #e0f2fe, #dbeafe)',
    purple: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
    green: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
    amber: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
  }[p.$tone] || '#f8fafc')};
  border: 1px solid ${p => ({
    blue: '#bfdbfe',
    purple: '#ddd6fe',
    green: '#a7f3d0',
    amber: '#fde68a',
  }[p.$tone] || '#e2e8f0')};
  border-radius: 20px;
  color: #0f172a;
  min-height: 112px;
  padding: 14px;
  text-align: left;
  width: 100%;

  span {
    align-items: center;
    background: white;
    border-radius: 16px;
    display: flex;
    font-size: 1.05rem;
    height: 40px;
    justify-content: center;
    margin-bottom: 12px;
    width: 40px;
  }

  strong { display: block; font-size: 0.9rem; }
  small { color: #64748b; display: block; font-size: 0.72rem; font-weight: 800; margin-top: 4px; }
`;

const PriorityCard = styled.section`
  background: linear-gradient(135deg, #fff7ed, #fff1f2);
  border: 1px solid #fed7aa;
  border-radius: 26px;
  box-shadow: 0 14px 34px rgba(248, 113, 113, 0.09);
  padding: 16px;
`;

const RecentCard = styled.section`
  background: white;
  border: 1px solid #e8f0fe;
  border-radius: 26px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
  padding: 16px;
`;

const SectionHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;

  span { color: #64748b; display: block; font-size: 0.7rem; font-weight: 900; letter-spacing: 0.1em; text-transform: uppercase; }
  h3 { color: #0f172a; font-size: 1.04rem; margin: 3px 0 0; }
`;

const TextButton = styled.button`
  background: transparent;
  border: 0;
  color: #0284c7;
  font-size: 0.78rem;
  font-weight: 900;
  padding: 8px 0 8px 8px;
`;

const PriorityBody = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;

  strong { display: block; font-size: 0.96rem; }
  p { color: #64748b; font-size: 0.78rem; margin: 3px 0 7px; }
`;

const ClientAvatar = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #2563eb, #0ea5e9);
  border-radius: 18px;
  color: white;
  display: flex;
  flex: 0 0 auto;
  font-size: 0.8rem;
  font-weight: 950;
  height: 46px;
  justify-content: center;
  width: 46px;
`;

const RiskPill = styled.span`
  background: ${p => (p.level === 'high' ? '#fee2e2' : p.level === 'medium' ? '#fef3c7' : '#dcfce7')};
  border-radius: 999px;
  color: ${p => (p.level === 'high' ? '#b91c1c' : p.level === 'medium' ? '#92400e' : '#166534')};
  display: inline-flex;
  font-size: 0.68rem;
  font-weight: 950;
  padding: 4px 9px;
  text-transform: capitalize;
`;

const Reason = styled.p`
  color: #7f1d1d;
  font-size: 0.8rem;
  line-height: 1.45;
  margin: 12px 0 0;
`;

const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const ResultItem = styled.button`
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  display: flex;
  gap: 11px;
  min-height: 66px;
  padding: 10px;
  text-align: left;
  width: 100%;
`;

const ResultCopy = styled.div`
  flex: 1;
  min-width: 0;

  strong { color: #0f172a; display: block; font-size: 0.9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  span { color: #64748b; display: block; font-size: 0.74rem; margin-top: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const ResultStatus = styled.span`
  background: ${p => (p.$paid ? '#dcfce7' : '#e0f2fe')};
  border-radius: 999px;
  color: ${p => (p.$paid ? '#166534' : '#075985')};
  flex: 0 0 auto;
  font-size: 0.66rem;
  font-weight: 950;
  padding: 6px 9px;
`;

const EmptyState = styled.div`
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 20px;
  padding: 18px;
  text-align: center;

  strong { color: #0f172a; display: block; }
  p { color: #64748b; font-size: 0.82rem; line-height: 1.45; margin: 6px 0 0; }
`;

const PendingCard = styled.section`
  background: white;
  border: 1px solid #e8f0fe;
  border-radius: 28px;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.08);
  margin-top: 24px;
  padding: 26px 20px;
  text-align: center;

  h2 { color: #0f172a; margin: 10px 0 8px; }
  p { color: #64748b; line-height: 1.5; margin: 0 0 18px; }
`;

const PendingIcon = styled.div`
  align-items: center;
  background: #fffbeb;
  border-radius: 24px;
  display: flex;
  font-size: 2rem;
  height: 74px;
  justify-content: center;
  margin: 0 auto;
  width: 74px;
`;

const StatusChip = styled.span`
  background: #fef3c7;
  border-radius: 999px;
  color: #92400e;
  font-size: 0.76rem;
  font-weight: 900;
  padding: 8px 12px;
`;

const skeletonGradient = 'linear-gradient(90deg, #eef2f7 25%, #f8fafc 37%, #eef2f7 63%)';

const HeroSkeleton = styled.div`
  animation: shimmer 1.2s infinite linear;
  background: ${skeletonGradient};
  background-size: 400% 100%;
  border-radius: 30px;
  height: 184px;
`;

const MetricRow = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, 1fr);
`;

const MetricSkeleton = styled.div`
  animation: shimmer 1.2s infinite linear;
  background: ${skeletonGradient};
  background-size: 400% 100%;
  border-radius: 22px;
  height: 116px;
`;

const CardSkeleton = styled.div`
  animation: shimmer 1.2s infinite linear;
  background: ${skeletonGradient};
  background-size: 400% 100%;
  border-radius: 26px;
  height: 150px;

  @keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

export default MobileDashboard;
